import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const faqCategories = [
  {
    category: "Booking & Scheduling",
    icon: "ri-calendar-check-line",
    faqs: [
      {
        q: "How do I book a cleaning service?",
        a: "Simply fill out our online quote form, call us at +1 (206) 555-0100, or chat with us on WhatsApp. We'll respond within a few hours to confirm your booking and scheduling details.",
      },
      {
        q: "Can I reschedule or cancel my appointment?",
        a: "Yes, you can reschedule or cancel with at least 24 hours notice at no charge. We understand business operations can change and are happy to accommodate schedule adjustments.",
      },
      {
        q: "Do you offer recurring cleaning contracts?",
        a: "Absolutely! We offer daily, weekly, bi-weekly, and monthly cleaning contracts for commercial clients. Recurring clients receive priority scheduling and preferred pricing.",
      },
    ],
  },
  {
    category: "Services & Scope",
    icon: "ri-sparkling-2-line",
    faqs: [
      {
        q: "What types of commercial spaces do you clean?",
        a: "We clean medical clinics, dental offices, corporate offices, car dealership showrooms, warehouses, retail stores, restaurants, and more. No space is too large or complex.",
      },
      {
        q: "What is included in a standard commercial cleaning?",
        a: "Our standard commercial cleaning covers all workspaces, restrooms, lobbies, break rooms, kitchens, floors, and trash removal. We tailor each service to your facility's specific needs.",
      },
      {
        q: "Do you clean medical and healthcare facilities?",
        a: "Yes! We specialize in medical-grade cleaning for clinics, dental offices, and healthcare facilities. Our team follows strict sanitation protocols and uses hospital-grade disinfectants.",
      },
    ],
  },
  {
    category: "Service Area & Coverage",
    icon: "ri-map-pin-2-line",
    faqs: [
      {
        q: "What areas do you serve?",
        a: "We serve the Greater Seattle area including Seattle, Bellevue, Redmond, Kirkland, Bothell, Edmonds, Renton, and surrounding communities in Washington State.",
      },
      {
        q: "Do you serve large commercial properties outside Seattle?",
        a: "Yes, for large commercial contracts we can travel to most areas in Western Washington. Contact us to discuss your specific location and requirements.",
      },
    ],
  },
  {
    category: "Pricing & Payment",
    icon: "ri-price-tag-3-line",
    faqs: [
      {
        q: "How is pricing determined?",
        a: "Pricing is based on square footage, type of facility, frequency of service, and specific cleaning requirements. We provide a free, no-obligation custom quote for all commercial clients.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit/debit cards, ACH bank transfers, Zelle, checks, and invoicing for commercial accounts. Payment terms are flexible for recurring contracts.",
      },
    ],
  },
  {
    category: "Quality & Guarantee",
    icon: "ri-shield-check-line",
    faqs: [
      {
        q: "Is your work guaranteed?",
        a: "Absolutely. We offer a 100% satisfaction guarantee. If you're not fully happy with our work, we'll return within 24 hours and re-clean the areas in question at no additional cost.",
      },
      {
        q: "Are your cleaners background-checked and insured?",
        a: "Yes. Every team member passes a thorough background check before joining our team. We are fully licensed, bonded, and insured for complete peace of mind.",
      },
    ],
  },
  {
    category: "Supplies & Equipment",
    icon: "ri-leaf-line",
    faqs: [
      {
        q: "Do you provide your own cleaning supplies?",
        a: "Yes! We bring all necessary professional-grade, eco-friendly cleaning supplies and commercial equipment. You don't need to provide anything — just enjoy your clean space.",
      },
      {
        q: "Do you use eco-friendly products?",
        a: "Yes, we prioritize eco-friendly, non-toxic cleaning products that are safe for people, pets, and the environment. We can also accommodate specific product requirements for sensitive environments like medical facilities.",
      },
    ],
  },
];

const FAQPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState<string | null>("0-0");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }}>
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 pb-20 bg-[#0F2A44] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20clean%20geometric%20pattern%20deep%20navy%20blue%20background%2C%20gold%20lines%20and%20circles%20minimal%20luxury%20design%2C%20high%20resolution%20professional%20texture%2C%20elegant%20corporate%20visual&width=1920&height=600&seq=faqbg001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-4 block">
            {t("faq_label")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5">
            {t("faq_headline")}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t("faq_sub")}
          </p>
          <div className="flex justify-center mt-8 text-sm text-white/50">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors duration-200">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-[#D4AF37]">FAQ</span>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-8">
            {faqCategories.map((cat, ci) => (
              <div key={ci}>
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#D4AF37]/20">
                    <i className={`${cat.icon} text-base text-[#D4AF37]`} />
                  </div>
                  <h2 className="text-lg font-bold text-[#0F2A44]">{cat.category}</h2>
                </div>
                {/* FAQ Items */}
                <div className="space-y-2">
                  {cat.faqs.map((faq, fi) => {
                    const key = `${ci}-${fi}`;
                    const isOpen = openKey === key;
                    return (
                      <div
                        key={fi}
                        className="bg-white rounded-xl overflow-hidden"
                        style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.04)" }}
                      >
                        <button
                          onClick={() => setOpenKey(isOpen ? null : key)}
                          className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer group"
                        >
                          <span className={`font-semibold text-sm md:text-base transition-colors duration-200 pr-4 ${isOpen ? "text-[#D4AF37]" : "text-[#0F2A44] group-hover:text-[#D4AF37]"}`}>
                            {faq.q}
                          </span>
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all duration-300 ${isOpen ? "bg-[#D4AF37] text-[#0F2A44] rotate-45" : "bg-[#0F2A44]/5 text-[#0F2A44]"}`}>
                            <i className="ri-add-line text-sm font-bold" />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-5">
                            <div className="h-px bg-[#0F2A44]/5 mb-4" />
                            <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still have questions? */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D4AF37]/15 mx-auto mb-5">
            <i className="ri-question-answer-line text-3xl text-[#D4AF37]" />
          </div>
          <h2 className="text-2xl font-bold text-[#0F2A44] mb-3">Still Have Questions?</h2>
          <p className="text-gray-500 text-base mb-8">Our team is ready to help. Reach out and we'll get back to you within a few hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#B8952E] text-[#0F2A44] font-bold px-8 py-3.5 rounded-full text-sm transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Contact Us
              <i className="ri-arrow-right-line" />
            </button>
            <a
              href="sms:+12064744668"
              className="inline-flex items-center justify-center gap-2 bg-[#0F2A44] hover:bg-[#1a3d5c] text-white font-semibold px-8 py-3.5 rounded-full text-sm transition-all duration-200 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-message-2-line" />
              Text Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default FAQPage;
